import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { label: 'Services', path: '/services-overview', icon: 'Briefcase' },
    { label: 'About', path: '/about-us', icon: 'Building2' },
    { label: 'Team', path: '/team-profiles', icon: 'Users' },
    { label: 'Contact', path: '/contact-consultation-request', icon: 'MessageSquare' },
    { label: 'Agent', path: '/agent', icon: 'Cpu' },
  ];

  const isActivePath = (path) => {
    if (path === '/services-overview') {
      return location.pathname === '/services-overview' || location.pathname === '/individual-service-detail';
    }
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-smooth ${
          isScrolled ? 'shadow-soft py-3' : 'py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/homepage" 
              className="flex items-center space-x-3 transition-smooth hover:opacity-80"
              onClick={closeMobileMenu}
            >
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
                <Icon name="Zap" size={20} color="white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-foreground">RCEG AI</span>
                <span className="text-xs text-muted-foreground font-medium">Ventures</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-smooth ${
                    isActivePath(item.path)
                      ? 'text-primary bg-primary/5' :'text-foreground hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = '/contact-consultation-request'}
              >
                Get Started
              </Button>
              <Button 
                variant="default" 
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => window.location.href = '/contact-consultation-request'}
              >
                Book Consultation
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg text-foreground hover:bg-muted transition-smooth"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-elevation-lg z-50 transform transition-smooth lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                <Icon name="Zap" size={16} color="white" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-foreground">RCEG AI</span>
                <span className="text-xs text-muted-foreground font-medium">Ventures</span>
              </div>
            </div>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-lg text-foreground hover:bg-muted transition-smooth"
              aria-label="Close mobile menu"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 px-6 py-6">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-smooth ${
                    isActivePath(item.path)
                      ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </nav>

          {/* Mobile CTA */}
          <div className="p-6 border-t border-border space-y-3">
            <Button 
              variant="outline" 
              fullWidth
              onClick={() => {
                closeMobileMenu();
                window.location.href = '/contact-consultation-request';
              }}
            >
              Get Started
            </Button>
            <Button 
              variant="default" 
              fullWidth
              iconName="Calendar"
              iconPosition="left"
              onClick={() => {
                closeMobileMenu();
                window.location.href = '/contact-consultation-request';
              }}
            >
              Book Consultation
            </Button>
          </div>
        </div>
      </div>

      {/* Header Spacer */}
      <div className={`${isScrolled ? 'h-16' : 'h-20'} transition-all duration-200`} />
    </>
  );
};

export default Header;
